import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Download, Search, ChevronLeft, ChevronRight, Eye } from 'lucide-react';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

interface Obligation {
  id: number;
  title: string;
  description: string;
  legal_source: string;
  owner: string;
  due_date: string;
  status: string;
  risk_rating: string;
  review_frequency: string;
  next_review_date: string;
  evidence_count: number;
  controls_count: number;
  capa_count: number;
  created_at: string;
  updated_at: string;
}

interface DashboardStats {
  compliant: number;
  partially_compliant: number;
  non_compliant: number;
  not_assessed: number;
  overdue: number;
  total: number;
}

const statusColors: Record<string, string> = {
  compliant: 'bg-green-100 text-green-800',
  partially_compliant: 'bg-yellow-100 text-yellow-800',
  non_compliant: 'bg-red-100 text-red-800',
  not_assessed: 'bg-gray-100 text-gray-800',
};

const riskColors: Record<string, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const EMPTY_FORM = {
  title: '',
  description: '',
  legal_source: '',
  owner: '',
  due_date: '',
  status: 'not_assessed',
  risk_rating: 'medium',
  review_frequency: 'quarterly',
};

const ObligationRegister: React.FC = () => {
  const [obligations, setObligations] = useState<Obligation[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [createModal, setCreateModal] = useState(false);
  const [detailModal, setDetailModal] = useState<Obligation | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (riskFilter) params.set('risk_rating', riskFilter);

      const [listRes, statsRes] = await Promise.all([
        fetch(`/api/compliance/obligations?${params}`, { headers: authHeaders() }).then(r => r.json()),
        fetch('/api/compliance/obligations/dashboard', { headers: authHeaders() }).then(r => r.json()),
      ]);
      setObligations(listRes.obligations || []);
      setTotalPages(listRes.totalPages || 1);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [page, search, statusFilter, riskFilter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/obligations', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to create obligation');
      setCreateModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const exportCsv = () => {
    const headers = ['Title', 'Legal Source', 'Owner', 'Due Date', 'Status', 'Risk Rating'];
    const rows = obligations.map(o => [o.title, o.legal_source, o.owner, o.due_date, o.status, o.risk_rating]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'obligation_register.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Obligation Register</h1>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          <button onClick={exportCsv} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <Download size={16} /> Export CSV
          </button>
          <button onClick={() => { setForm(EMPTY_FORM); setCreateModal(true); }} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> Add Obligation
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Compliant', value: stats.compliant, color: 'text-green-600' },
            { label: 'Partially Compliant', value: stats.partially_compliant, color: 'text-yellow-600' },
            { label: 'Non-Compliant', value: stats.non_compliant, color: 'text-red-600' },
            { label: 'Not Assessed', value: stats.not_assessed, color: 'text-gray-600' },
            { label: 'Overdue', value: stats.overdue, color: 'text-red-700' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search obligations..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="border rounded-lg pl-9 pr-3 py-2 text-sm w-full"
          />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="border rounded-lg px-3 py-2 text-sm">
          {['', 'compliant', 'partially_compliant', 'non_compliant', 'not_assessed'].map(s => (
            <option key={s} value={s}>{s ? s.replace(/_/g, ' ') : 'All Statuses'}</option>
          ))}
        </select>
        <select value={riskFilter} onChange={e => { setRiskFilter(e.target.value); setPage(1); }} className="border rounded-lg px-3 py-2 text-sm">
          {['', 'low', 'medium', 'high', 'critical'].map(r => (
            <option key={r} value={r}>{r || 'All Risk Levels'}</option>
          ))}
        </select>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : obligations.length === 0 ? (
          <div className="py-12 text-gray-500">No obligations found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Title', 'Legal Source', 'Owner', 'Due Date', 'Status', 'Risk', 'Linked', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {obligations.map(o => {
                const overdue = o.due_date && new Date(o.due_date) < new Date() && o.status !== 'compliant';
                return (
                  <tr key={o.id} className={`hover:bg-gray-50 ${overdue ? 'bg-red-50/50' : ''}`}>
                    <td className="px-4 py-3 text-left font-medium text-gray-900 max-w-xs truncate">{o.title}</td>
                    <td className="px-4 py-3 text-left text-gray-600 max-w-xs truncate">{o.legal_source || '—'}</td>
                    <td className="px-4 py-3 text-left text-gray-600">{o.owner || '—'}</td>
                    <td className="px-4 py-3 text-left text-gray-600">
                      {o.due_date ? new Date(o.due_date).toLocaleDateString() : '—'}
                      {overdue && <span className="ml-1 text-xs text-red-600 font-medium">OVERDUE</span>}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[o.status] || 'bg-gray-100 text-gray-700'}`}>
                        {o.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${riskColors[o.risk_rating] || 'bg-gray-100 text-gray-700'}`}>
                        {o.risk_rating}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left text-xs text-gray-500">
                      {o.evidence_count > 0 && <span className="mr-2">{o.evidence_count} evidence</span>}
                      {o.controls_count > 0 && <span className="mr-2">{o.controls_count} controls</span>}
                      {o.capa_count > 0 && <span>{o.capa_count} CAPAs</span>}
                      {!o.evidence_count && !o.controls_count && !o.capa_count && '—'}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <button onClick={() => setDetailModal(o)} className="text-blue-600 hover:text-blue-800">
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Create Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">New Obligation</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Legal Source</label>
                  <input value={form.legal_source} onChange={e => setForm(f => ({ ...f, legal_source: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. GDPR Art. 5" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                  <input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Review Frequency</label>
                  <select value={form.review_frequency} onChange={e => setForm(f => ({ ...f, review_frequency: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {['monthly', 'quarterly', 'semi_annually', 'annually'].map(rf => (
                      <option key={rf} value={rf}>{rf.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {['not_assessed', 'compliant', 'partially_compliant', 'non_compliant'].map(s => (
                      <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Risk Rating</label>
                  <select value={form.risk_rating} onChange={e => setForm(f => ({ ...f, risk_rating: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {['low', 'medium', 'high', 'critical'].map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreate} disabled={submitting || !form.title.trim()} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create Obligation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <h2 className="text-lg font-semibold text-gray-900">{detailModal.title}</h2>
                <button onClick={() => setDetailModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
              </div>
              {detailModal.description && <p className="text-sm text-gray-600">{detailModal.description}</p>}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Legal Source</p>
                  <p className="font-medium text-gray-900">{detailModal.legal_source || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Owner</p>
                  <p className="font-medium text-gray-900">{detailModal.owner || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Due Date</p>
                  <p className="font-medium text-gray-900">{detailModal.due_date ? new Date(detailModal.due_date).toLocaleDateString() : '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[detailModal.status]}`}>
                    {detailModal.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500">Risk Rating</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${riskColors[detailModal.risk_rating]}`}>
                    {detailModal.risk_rating}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500">Review Frequency</p>
                  <p className="font-medium text-gray-900 capitalize">{detailModal.review_frequency?.replace(/_/g, ' ') || '—'}</p>
                </div>
              </div>
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Linked Items</h3>
                <div className="flex gap-4 text-sm text-gray-600">
                  <span>{detailModal.evidence_count || 0} Evidence</span>
                  <span>{detailModal.controls_count || 0} Controls</span>
                  <span>{detailModal.capa_count || 0} CAPAs</span>
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button onClick={() => setDetailModal(null)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObligationRegister;
